/**
 * Platform-owned Player AI contracts shared by the shell, Player AI client,
 * and product-service adapters (ADR-0074).
 *
 * These are semantic contracts. They deliberately contain no tenant id,
 * route, URL, HTTP method, database field, permission claim, or canvas patch.
 * Tenant and authorization always come from the validated request context;
 * the shell resolves entity references through its registered manifests.
 */
export const PLATFORM_MODULE_IDS = [
    "solve",
    "vsm",
    "hoshin",
    "lama",
    "kpi",
];
export const PLATFORM_ENTITY_TYPES = {
    solve: ["solve", "widget"],
    vsm: ["vsm", "improvement"],
    hoshin: ["hoshin_plan", "objective", "target", "x_matrix_item"],
    lama: [
        "questionnaire",
        "campaign",
        "wave",
        "assignment",
        "assignment_response",
        // A question ref names one allocated question under its assignment parent
        // (the response-save target); it is not independently navigable.
        "question",
        // A report is campaign-derived: its entity_id IS the campaign id until
        // LAMA gains a separately persisted report entity (plan D6).
        "report",
    ],
    kpi: ["kpi"],
};
export const PLATFORM_PAGE_SURFACES = [
    "home",
    "launcher",
    "notifications",
    "settings",
    "admin",
    "module",
    "player_ai",
];
export const CURRENT_PAGE_CONTEXT_LIMITS = {
    filters: 10,
    filterValues: 20,
    selectedRefs: 20,
    textLength: 200,
};
/**
 * The seven versioned LAMA semantic reads (plan §7.1). Playbook BE owns the
 * projections and reauthorizes every call; Player AI only selects them.
 * `report_id` is the campaign id (D6), and `lama.assignment.get@1` answers only
 * the caller's own assignment.
 */
export const LAMA_READ_TOOL_IDS = [
    "lama.questionnaire.get@1",
    "lama.campaign.get@1",
    "lama.campaign.progress.get@1",
    "lama.assignment.get@1",
    "lama.reports.list@1",
    "lama.report.get@1",
    "lama.report.responses.list@1",
];
export const PLATFORM_READ_TOOL_IDS = [
    "my_work.snapshot@1",
    "my_work.list@1",
    "my_work.get@1",
    "my_work.changes@1",
    "platform.search@1",
    "platform.navigate@1",
    "notifications.list@1",
    "notifications.get@1",
    "notifications.unread_count@1",
    "settings.get@1",
    "admin.search@1",
    "admin.get@1",
    "admin.compare@1",
    "admin.audit.list@1",
    ...LAMA_READ_TOOL_IDS,
];
export const ADMIN_READ_PROVIDER_IDS = [
    "members",
    "organization",
    "identity",
    "modules",
    "communication",
    "readiness",
    "audit",
];
export const PLATFORM_ACTION_KINDS = [
    "platform.preference.update@1",
    "notification.mark_read@1",
    "notification.mark_unread@1",
    "notification.mark_all_read@1",
    "notification.acknowledge@1",
    "notification.preference.update@1",
    "resource_audience.attach@1",
    "resource_audience.detach@1",
    "vsm.improvement.update_owner@1",
    "vsm.improvement.update_due_date@1",
    "vsm.improvement.update_status@1",
    "hoshin.objective.update_owner@1",
    "hoshin.target.update_by_date@1",
    "hoshin.item.update_rag@1",
    "lama.assignment_response.update_comment@1",
    "lama.assignment_response.save@1",
    "lama.assignment.submit@1",
];
export const PLATFORM_ACTION_OWNERS = [
    "platform",
    "notification",
    "vsm",
    "hoshin",
    "lama",
];
export const PLATFORM_PRESENTATION_PREFERENCES = [
    "theme",
    "locale",
    "time_zone",
];
/**
 * Theme values the platform presentation preference stores. One vocabulary
 * for Playbook BE (validator, DB check), the shell (store, controls) and the
 * Player AI action path; `high-contrast` maps to the design system's
 * `.pb-high-contrast` palette (ADR-0076, PB-5134). `system` is "Match device":
 * the shell resolves it from the device's contrast and colour-scheme requests.
 */
export const PLATFORM_THEMES = [
    "dark",
    "light",
    "high-contrast",
    "system",
];
/** The theme a user has before they pick one: Match device. */
export const DEFAULT_PLATFORM_THEME = "system";
/**
 * Launcher preference vocabulary shared by Playbook BE's validator and the
 * shell (PB-5134, d22). Favourites and the default landing are validated by
 * FORMAT only: the launcher roster (which module ids exist and are enabled)
 * belongs to the shell, which ignores ids it does not know.
 */
export const PLATFORM_LAUNCHER_FAVOURITES_MAX = 24;
export const PLATFORM_LAUNCHER_ID_PATTERN = /^[a-z][a-z0-9-]{0,63}$/;
export const PLATFORM_LAUNCHER_FIXED_LANDINGS = ["home", "launcher"];
export const NOTIFICATION_CHANNELS = ["in_app", "email"];
export const SUPPORTED_RESOURCE_AUDIENCE_SHARE_KINDS = [
    {
        resource_module: "solve",
        resource_entity_type: "solve",
        audience_type: "vsm",
        role: "viewer",
    },
];
/**
 * One evidence pointer on a LAMA response-save action — never the blob. The
 * vocabulary and bounds mirror LAMA's own `SaveResponseEvidenceDto`; the owner
 * service remains the authority and re-validates every field.
 */
export const LAMA_RESPONSE_EVIDENCE_REF_KINDS = [
    "link",
    "file",
    "metric",
];
/** X-Matrix items are addressed by kind; the owner refuses an unnamed one. */
export const HOSHIN_X_MATRIX_ITEM_KINDS = [
    "objective",
    "priority",
    "target",
];
export const PLATFORM_ACTION_SAFE_ERROR_CODES = [
    "not_authorized",
    "not_found",
    "stale",
    "invalid",
    "unavailable",
    "conflict",
];
//# sourceMappingURL=platform-player-ai.js.map